/**
 * Adapters between AllergyForm's Allergy v2 body shape (camelCase, sent
 * to api-allergy-v2-service today) and the transcript's captured-item
 * payload shape (snake_case, matches what the Python agent emits and
 * what Go's SaveSection fan-out reads on Save & next).
 *
 * Same pattern as complaintBodyAdapters / diagnosisBodyAdapters /
 * medicineBodyAdapters / hospitalisationBodyAdapters /
 * labInvestigationBodyAdapters.
 *
 *   Allergy v2 add body      → captured "add" payload
 *   {name, category, catalogId, mCode, source,
 *    reactions:[{name, source}], firstNoted, lastReaction?,
 *    description}
 *      →
 *   {substance, name, category, reactions, since_date, description}
 *
 * Simplifications for the transcript flow:
 *   - The AI-side `add_allergy` tool only carries `substance`, `category`
 *     (Salt|Food|Other), `reactions` (comma string), `since_date`,
 *     `description`. It never resolves a catalog id / mCode, so those
 *     stay blank on the round-trip; the Go adapter forces
 *     `source="free-text"` on the wire.
 *   - Reactions are round-tripped as an array of {name, source} objects.
 *     The AI's comma-string is normalised into the same shape at seed
 *     time by AllergyForm (seedReactionsFromPayload), so the review
 *     board sees consistent chips regardless of input path.
 */

const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

/**
 * Allergy v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const rxs = Array.isArray(body.reactions) ? body.reactions : [];
  return {
    substance:   body.name || "",
    name:        body.name || "",
    category:    body.category || "Other",
    reactions:   rxs.map((r) => ({
      name:   (r && r.name) || "",
      source: (r && r.source) || "free-text",
    })).filter((r) => r.name),
    since_date:         toIsoDate(body.firstNoted),
    // `last_reaction_date` mirrors `since_date` — the AI emits it on the
    // same `add_allergy` tool call, the Go fan-out reads it on Save &
    // next (adapters/allergies.go) and the Shift-2 effective overlay in
    // CapturedSections' reedit render reseeds the Last-reaction stepper
    // from it on reopen. Omitting it here meant reviewer-typed /
    // AI-captured values were silently dropped on commit, so reopens
    // showed empty and the saved Allergy row carried no last-reaction
    // date.
    last_reaction_date: toIsoDate(body.lastReaction),
    description:        body.description || "",
  };
}

/**
 * Allergy v2 update body → captured "update" payload.
 *
 * AllergyForm's update-mode submit intentionally omits `name` from the
 * body (identity is preserved server-side by the Allergy v2 update
 * endpoint from the existing row). But a captured item persisted via
 * the transcript's propose+commit flow stores the payload verbatim —
 * no v2 round-trip to re-fetch the name. Without the identity fields
 * the staged row renders as "(untitled)" and the Save & next fan-out
 * posts a nameless add.
 *
 * Pull `name` / `category` from the context's `record` (the matched
 * Allergy v2 row passed by saveAiProposalAsUpdate) so the captured
 * carries the same identity the row-click path would've produced.
 * Reported mid-ZY-10387 Alg rollout — AI-match-to-DB "Update" on an
 * existing Dust row created a new "(untitled)" entry instead of
 * routing as an update on Dust.
 */
export function bodyToCapturedUpdate(body, context) {
  const base = bodyToCapturedAdd(body);
  const record = (context && context.record) || null;
  if (record) {
    const name = record.name || "";
    if (name) {
      base.substance = name;
      base.name = name;
    }
    if (!base.category && record.category) {
      base.category = record.category;
    }
  }
  return base;
}
